import { DoneIcon, QuestionCircleIcon } from '@mm/icons';
import { ReactComponent as InstagramIcon } from 'assets/instagram.svg';
import { ReactComponent as FBIcon } from 'assets/messenger-fb-new.svg';
import { ReactComponent as SMSIcon } from 'assets/smartphone.svg';
import { ReactComponent as WebchatIcon } from 'assets/webchat-new.svg';
import classnames from 'classnames';
import React, { useMemo } from 'react';
import { SegmentedMessage } from 'sms-segments-calculator';
import {
  ButtonColor,
  PrimaryButton,
} from '../../buttons/PrimaryButton/PrimaryButton';
import { getUpdatedTextWithAttribute } from '@mm/state/helpers/attribute-picker-helpers';
import AttributePicker from '../AttributePicker/AttributePicker';
import { getUpdatedTextWithEmoji } from '../EmojiPicker/emoji-picker-helpers';
import { EmojiPicker } from '../EmojiPicker/EmojiPicker';
import { PrimaryToolTip } from '../PrimaryToolTip/PrimaryToolTip';
import './TextAreaSidePanel.pcss';

type Props = {
  maxTextLength?: number;
  currentTextLength?: number;
  text: string;
  updateText: (newText: string) => any;
  passedRef: any;
  onSaveClick: () => void;
  extraTextLength: number;
  className?: string;
  showTextLength?: boolean;
  showEmojiPicker?: boolean;
  emojiPickerColor?: string;
  attributePickerColor?: string;
  showAttributePicker?: boolean;
  orientation?: 'horizontal' | 'vertical';
  showOnlyNameAttributes?: boolean;
  countByChannel?: boolean;
  newCharacterCountDesign?: boolean;
  maxSmsChannelTextLength?: number;
  maxOthersChannelTextLength?: number;
  hasQQButtons?: boolean;
  smsStructure?: string;
  isAutomationsSlideout?: boolean;
};

export const TextAreaSidePanel = (props: Props) => {
  const {
    maxTextLength,
    text,
    updateText,
    passedRef,
    className,
    showTextLength = true,
    showEmojiPicker = true,
    emojiPickerColor = '#F8F3FF',
    attributePickerColor = '#F8F3FF',
    showAttributePicker = true,
    orientation = 'vertical',
    currentTextLength = 0,
    showOnlyNameAttributes = false,
    newCharacterCountDesign = false,
    countByChannel = false,
    maxSmsChannelTextLength,
    maxOthersChannelTextLength,
    onSaveClick,
    extraTextLength,
    hasQQButtons,
    smsStructure,
    isAutomationsSlideout,
  } = props;
  const baseClass =
    orientation === 'horizontal' || newCharacterCountDesign
      ? 'text-area-side-panel--horizontal'
      : 'text-area-side-panel';
  const classList = classnames(baseClass, className, {
    'text-area-side-panel--new-design': newCharacterCountDesign,
    'text-area-side-panel--sms-related': countByChannel,
  });

  const onEmojiPicked = (emoji: string) => {
    const newText = getUpdatedTextWithEmoji(text, emoji, passedRef);
    if (
      !maxTextLength ||
      newText.length <= maxTextLength ||
      newText.length < currentTextLength
    ) {
      updateText(newText);
    }
  };

  const onAttributePicked = (attribute: string) => {
    const newText = getUpdatedTextWithAttribute(
      text,
      attribute,
      passedRef,
      showOnlyNameAttributes,
    );

    if (
      !maxTextLength ||
      newText.length <= maxTextLength ||
      newText.length < currentTextLength
    ) {
      updateText(newText);
    }
  };

  const tooltipText = hasQQButtons
    ? 'Character count is higher on SMS since we add "Reply 1 for [Option 1]' +
      ' and 2 for [Option 2], etc. SMS messages that exceed 160 characters will be sent as MMS'
    : 'SMS messages that exceed 160 characters will be sent as MMS.';

  // Note: need this to be able to do non-stale updates to calculaitons
  const tailText = useMemo(() => {
    if (!smsStructure) {
      return '';
    }
    return smsStructure.replace(text, '');
  }, [smsStructure]);

  const textForSmsSegmentsCalculations = text + tailText;
  const textForSmsSegmentsCalculationsLenght =
    textForSmsSegmentsCalculations.length;

  const { segmentsCount, encodingName } = useMemo(() => {
    const { segmentsCount, encodingName } = new SegmentedMessage(
      textForSmsSegmentsCalculations,
    );
    return { segmentsCount, encodingName };
  }, [textForSmsSegmentsCalculations]);

  // Note: number after 1600 are not really precise,
  // but we cover vast majority of cases.
  // We can get more pricese later if needed.
  const countedCredits =
    textForSmsSegmentsCalculationsLenght < 160
      ? segmentsCount * 10
      : textForSmsSegmentsCalculationsLenght < 800
      ? 20
      : textForSmsSegmentsCalculationsLenght < 1600
      ? encodingName === 'GSM-7'
        ? 20
        : 40
      : 40;

  const segmentsTooltipText = `SMS messages can have up to 160 simple characters for 10 credits. Including Emoji reduces this to 70 characters. 

  Warning for Mac users: "Smart quotes" can reduce this limit to 70 as well. `;

  const smsCountComponent = (
    <div className="text-area-side-panel__sms-count-component">
      <PrimaryToolTip
        useHover={true}
        anchorElement={
          <div className="text-area-side-panel__channel-count">
            <SMSIcon className="text-area-side-panel__channel-icon" />
            <span
              className={classnames({
                'text-area-side-panel__channel-count--yellow':
                  currentTextLength + extraTextLength > 160,
              })}
            >
              {currentTextLength + extraTextLength}/{maxSmsChannelTextLength}
            </span>
          </div>
        }
      >
        {tooltipText}
      </PrimaryToolTip>
      <div> - </div>
      <PrimaryToolTip
        useHover={true}
        anchorElement={
          <div className="text-area-side-panel__channel-count">
            <span
              className={classnames({
                'text-area-side-panel__channel-count--yellow':
                  currentTextLength + extraTextLength > 160,
              })}
            >
              {countedCredits} credits
            </span>
            <QuestionCircleIcon color="#f8f3ff" style={{ height: 14 }} />
          </div>
        }
      >
        {segmentsTooltipText}
      </PrimaryToolTip>
    </div>
  );

  const newCharacterCountComponents = (
    <>
      {showTextLength && countByChannel ? (
        <div className="text-area-side-panel__channel-count-containers">
          <div className="text-area-side-panel__channel-count-container">
            {!!maxSmsChannelTextLength && smsCountComponent}
          </div>
          <div className="text-area-side-panel__channel-count-container">
            {!!maxOthersChannelTextLength && (
              <div className="text-area-side-panel__channel-count">
                <FBIcon className="text-area-side-panel__channel-icon" />
                <WebchatIcon className="text-area-side-panel__channel-icon" />
                <InstagramIcon className="text-area-side-panel__channel-icon" />
                {currentTextLength}/{maxOthersChannelTextLength}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="text-area-side-panel__count-container">
          {!!maxTextLength && (
            <>
              {currentTextLength}/{maxTextLength}
            </>
          )}
        </div>
      )}
      {showAttributePicker && (
        <AttributePicker
          classNameForIcon="text-area-side-panel__attribute-icon"
          onAttributePicked={onAttributePicked}
          showOnlyNameAttributes={showOnlyNameAttributes}
        />
      )}

      {showEmojiPicker && (
        <EmojiPicker
          className="text-area-side-panel__emoji-picker"
          onEmojiPicked={onEmojiPicked}
          color={emojiPickerColor}
        />
      )}
      <div className="text-area-side-panel__save-button-container">
        <PrimaryButton
          color={ButtonColor.blue}
          onClick={onSaveClick}
          leftIcon={<DoneIcon />}
          size="sm"
        >
          Save
        </PrimaryButton>
      </div>
    </>
  );

  return (
    <div className={classList}>
      {newCharacterCountDesign ? (
        newCharacterCountComponents
      ) : (
        <>
          {showAttributePicker && (
            <AttributePicker
              classNameForIcon="text-area-side-panel__message-edit-icon"
              onAttributePicked={onAttributePicked}
              showOnlyNameAttributes={showOnlyNameAttributes}
              color={attributePickerColor}
            />
          )}

          {showEmojiPicker && (
            <EmojiPicker
              classNameForIcon={classnames(
                'text-area-side-panel__message-edit-icon',
                {
                  automations_slideout: isAutomationsSlideout,
                },
              )}
              onEmojiPicked={onEmojiPicked}
              color={emojiPickerColor}
            />
          )}

          {showTextLength && maxTextLength && (
            <div
              className={
                isAutomationsSlideout
                  ? 'text-area-side-panel__message-edit-counter--grey'
                  : classnames({
                      'text-area-side-panel__message-edit-counter':
                        maxTextLength - currentTextLength > 10,
                      'text-area-side-panel__message-edit-counter--yellow':
                        currentTextLength > 160 &&
                        maxTextLength - currentTextLength > 10,
                      'text-area-side-panel__message-edit-counter--red':
                        maxTextLength - currentTextLength <= 10,
                    })
              }
            >
              {currentTextLength}/{maxTextLength}
            </div>
          )}
        </>
      )}
    </div>
  );
};
